/**
 * CASTLE ATLAS — AI Prompt Engineering Skill Config
 * ─────────────────────────────────────────────────
 * This file owns ALL visual logic for the AI Prompting skill card.
 * Supabase owns content (name, desc, lessons, exercises, colors).
 * This file owns rendering (JSX, animations, card structure).
 *
 * FIXED (this pass):
 *   - wrapperProps only set `style:{cursor:'pointer'}` when onClick was
 *     provided, but the later explicit `style={{...}}` on the same
 *     element silently overwrote it (JSX doesn't merge style objects
 *     across a spread + a literal) — so the pointer cursor never
 *     actually applied. Moved cursor into the main style object,
 *     matching how the sales-negotiation and copywriting configs
 *     already do it.
 *   - The whole-card onClick made the card a div with a click handler
 *     and nothing else — not reachable or activatable by keyboard.
 *     Added role="button", tabIndex, and Enter/Space handling when
 *     onClick is passed. No visual change, no prop-interface change.
 *
 * FIXED (polish pass):
 *   - `progress:`/`completion:` used to read from a `progress` prop
 *     (raw exercise count, capped by `skill?.exercises ?? 15` — the
 *     ever-present "0/15" was that 15-fallback firing, since
 *     `skill.exercises` isn't actually 15 for the real curriculum of
 *     13 lessons x 5 exercises). Worse: both Dashboard.jsx and
 *     Skills.jsx compute that prop via
 *     `completedExercises.filter(ex => ex.skill_id === id)`, but no
 *     write path anywhere in the app (useProgress.js's markComplete,
 *     the only place any skill writes to user_progress) ever sets
 *     skill_id on a row — so that prop is structurally always 0 for
 *     every skill's card on both pages, not just this one. Rather than
 *     touch that shared filtering (used by all four cards, out of this
 *     file's scope), this card now ignores the prop and computes its
 *     own numbers the same way PromptLanding.jsx's ManifestPanel does:
 *     live completedExercises (own useProgress() call) filtered by
 *     lesson_id membership in this skill's own 13 lesson IDs — the
 *     same fields (`lessons: X/13`, `overall %`, `phases: N`), same
 *     formula, so this card can't drift from what the landing page
 *     shows. See PromptLanding.jsx (EXERCISES_PER_LESSON,
 *     FLATTENED_LESSONS, lessonsComplete/lessonsTotal/lessonsPct) for
 *     the source this mirrors.
 *   - Dot trail and progress bar were reading the same broken
 *     `progress`/`totalExercises` pair, so they visually agreed with
 *     the old metadata line (both always showing empty) without either
 *     being right. Now both derive from the same lessonsComplete/
 *     lessonsTotal computed above — 13 dots, one per lesson.
 *   - The local TOKENS object duplicated terminalTokens.js's shared TT
 *     palette almost value-for-value, but had drifted on radiusSm (8
 *     here vs TT.radius.sm=6) and had no equivalent for TT.radius.lg/
 *     xl distinction. Replaced with a direct TT import — no more
 *     hand-typed hex that can silently drift from the landing page's
 *     own palette.
 *
 * Used by:
 *   - Dashboard.jsx (active/progress card)
 *   - Skills.jsx (preview card)
 *
 * Drop path: frontend/src/data/configs/config.ai-prompting.js
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { TT, PHASES } from '@features/ai-prompting/terminalTokens';
import { useProgress } from '@hooks/useProgress';

/* ── Typing animation string ── */
const TYPING_STRING = 'generate({ model: "deepseek" })';

// Same source PromptLanding.jsx's ManifestPanel reads its own lessons/
// overall/phases numbers from — PHASES is the single source of truth for
// curriculum structure (6 phases, 13 lessons), so this can't disagree with
// the landing page about how many lessons exist, only about how many are
// done (and that's computed identically too, below).
const ALL_LESSON_IDS = PHASES.flatMap((phase) => phase.lessons);
const EXERCISES_PER_LESSON = 5;

/**
 * AiPromptingCard
 *
 * Props:
 *   skill         {object}  — row from Supabase skills table
 *   progress      {number}  — unused for the numbers shown (see file header);
 *                             accepted so callers passing it don't warn/break
 *   isDark        {boolean} — current theme (card is always dark, but passed for parity)
 *   mode          {string}  — 'dashboard' | 'skills'
 *   onClick       {fn}      — card click handler (navigate to landing)
 *   onAction      {fn}      — CTA button click (navigate to exercise or landing)
 */
export const AiPromptingCard = ({ skill, isDark, mode = 'skills', onClick, onAction }) => {
  const [hovered, setHovered]   = useState(false);
  const [cursor, setCursor]     = useState(true);
  const [typed, setTyped]       = useState('');
  const { completedExercises = [] } = useProgress();

  const getLessonDone = (id) => completedExercises.filter((e) => e.lesson_id === id).length;
  const lessonsTotal = ALL_LESSON_IDS.length; // 13
  const lessonsComplete = ALL_LESSON_IDS.filter((id) => getLessonDone(id) >= EXERCISES_PER_LESSON).length;
  const lessonsPct = lessonsTotal > 0 ? Math.round((lessonsComplete / lessonsTotal) * 100) : 0;
  const phaseCount = PHASES.length;

  /* Cursor blink */
  useEffect(() => {
    const id = setInterval(() => setCursor(c => !c), 530);
    return () => clearInterval(id);
  }, []);

  /* Typing animation on hover */
  useEffect(() => {
    if (!hovered) { setTyped(''); return; }
    let i = 0;
    const id = setInterval(() => {
      setTyped(TYPING_STRING.slice(0, i));
      i++;
      if (i > TYPING_STRING.length) clearInterval(id);
    }, 38);
    return () => clearInterval(id);
  }, [hovered]);

  const wrapperProps = onClick
    ? {
        onClick,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
      }
    : {};

  return (
    <div
      {...wrapperProps}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width:         '100%',
        background:    TT.bg,
        borderRadius:  TT.radius.xl,
        border:        `1px solid ${TT.border}`,
        overflow:      'hidden',
        fontFamily:    TT.fontMono,
        transition:    TT.trans,
        transform:     hovered ? 'translateY(-4px)' : 'none',
        boxShadow:     hovered
          ? `0 24px 48px ${TT.orangeGlow}, 0 0 0 1px rgba(234,88,12,0.2)`
          : '0 4px 16px rgba(0,0,0,0.4)',
        display:       'flex',
        flexDirection: 'column',
        cursor:        onClick ? 'pointer' : 'default',
      }}
    >
      {/* ── macOS title bar ── */}
      <div style={{
        background:   TT.bgSurface,
        borderBottom: `1px solid ${TT.border}`,
        padding:      '10px 16px',
        display:      'flex',
        alignItems:   'center',
        gap:          12,
      }}>
        <div style={{ display: 'flex', gap: 7 }}>
          {['#ff5f56', '#ffbd2e', '#27c93f'].map((c, i) => (
            <div key={i} style={{
              width: 12, height: 12, borderRadius: '50%', background: c,
              boxShadow: hovered ? `0 0 8px ${c}80` : 'none',
              transition: 'all 0.18s ease',
            }} />
          ))}
        </div>
        <span style={{ flex: 1, textAlign: 'center', fontSize: 11, color: '#555', letterSpacing: '0.05em', fontFamily: TT.fontMono }}>
          castle-atlas — prompt-engineering
        </span>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '18px 20px 0', flex: 1 }}>
        {/* Comment line */}
        <div style={{ marginBottom: 5 }}>
          <span style={{ color: '#666', fontSize: 12 }}>// </span>
          <span style={{ color: TT.green, fontSize: 12, fontFamily: TT.fontDisplay }}>{skill?.name ?? 'AI Prompt Engineering'}</span>
        </div>

        {/* Typing line */}
        <div style={{ marginBottom: 14 }}>
          <span style={{ color: TT.orange, fontSize: 12, fontWeight: 700 }}>const</span>
          <span style={{ color: '#e2e8f0', fontSize: 12 }}> skill = await ai.</span>
          {hovered
            ? <span style={{ color: TT.amber, fontSize: 12 }}>
                {typed}
                <span style={{ opacity: cursor ? 1 : 0, transition: 'opacity 0.1s' }}>_</span>
              </span>
            : <span style={{ color: TT.textMuted, fontSize: 12 }}>...</span>}
        </div>

        {/* Metadata lines — same fields/formula as PromptLanding's
            ManifestPanel (lessons/overall/phases), plus category
            (unchanged). `lessons` replaces the old `progress` line —
            see file header for why 0/15 was showing. */}
        <div style={{ borderTop: `1px solid ${TT.bgElevated}`, paddingTop: 12, marginBottom: 12 }}>
          {[
            ['lessons',    `${lessonsComplete}/${lessonsTotal}`,     TT.green],
            ['completion', `${lessonsPct}%`,                         TT.amber],
            ['category',   `"${skill?.category ?? 'TECHNOLOGY'}"`,   TT.purple],
            ['phases',     `${phaseCount}`,                          TT.cyan],
          ].map(([l, v, c]) => (
            <div key={l} style={{ display: 'flex', gap: 8, marginBottom: 5 }}>
              <span style={{ color: TT.textMuted, fontSize: 11, minWidth: 100 }}>{l}:</span>
              <span style={{ color: c,            fontSize: 11 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ background: TT.bgSurface, borderRadius: 2, height: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${lessonsPct}%`,
              background: `linear-gradient(90deg, ${TT.orange}, ${TT.amber})`,
              borderRadius: 2, transition: 'width 1s ease',
              boxShadow: lessonsPct > 0 ? `0 0 8px ${TT.orangeGlow}` : 'none',
            }} />
          </div>
        </div>

        {/* Dot trail — one dot per lesson (13), lit for each completed
            lesson. Was reading the same broken progress/totalExercises
            pair as the old metadata line above (see file header) — now
            shares the same lessonsComplete/lessonsTotal this card
            actually computes, so it lights up in step with the numbers
            instead of matching them only by coincidence at zero. */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {Array.from({ length: lessonsTotal }).map((_, i) => (
            <div key={i} style={{
              width: 6, height: 6, borderRadius: '50%',
              background: i < lessonsComplete ? TT.orange : TT.bgElevated,
              border:     i < lessonsComplete ? 'none' : `1px solid ${TT.border}`,
              boxShadow:  i < lessonsComplete ? `0 0 6px ${TT.orangeGlow}` : 'none',
            }} />
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <div style={{ background: '#111', borderTop: `1px solid ${TT.bgElevated}`, padding: '12px 16px' }}>
        {onAction ? (
          <button
            onClick={e => { e.stopPropagation(); onAction(); }}
            style={{
              width: '100%', padding: '11px 14px', minHeight: 44, boxSizing: 'border-box',
              background:   hovered ? TT.orange : `rgba(234,88,12,0.12)`,
              color:        hovered ? '#fff' : TT.orange,
              border:       `1px solid rgba(234,88,12,0.3)`,
              borderRadius: TT.radius.sm,
              fontWeight: 700, fontSize: 12, cursor: 'pointer',
              transition: TT.trans,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              fontFamily: TT.fontMono,
            }}
          >
            {lessonsPct > 0 ? `> continue · ${lessonsPct}%` : '> ./start_skill.sh'}
            <ArrowRight size={12} />
          </button>
        ) : (
          <Link
            to={skill?.route ?? '/skills/ai-prompting'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              padding: '11px 14px', minHeight: 44, boxSizing: 'border-box',
              background:   hovered ? TT.orange : `rgba(234,88,12,0.12)`,
              color:        hovered ? '#fff' : TT.orange,
              border:       `1px solid rgba(234,88,12,0.3)`,
              borderRadius: TT.radius.sm,
              fontWeight: 700, fontSize: 12, textDecoration: 'none',
              transition: TT.trans, fontFamily: TT.fontMono,
            }}
          >
            {lessonsPct > 0 ? `> continue · ${lessonsPct}%` : '> ./start_skill.sh'}
            <ArrowRight size={12} />
          </Link>
        )}
      </div>
    </div>
  );
};

/* ── Shape/style tokens exported for the universal card resolver ── */
export const SHAPE = 'terminal';
export const SKILL_ID = 'ai-prompting';

export default AiPromptingCard;
